import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuthStore } from '../store';
import { formatDistanceToNow, formatTime } from '../lib/utils';

interface Chat {
  id: string;
  name?: string;
  type: 'dm' | 'group';
  lastMessage?: {
    content: string;
    createdAt: string;
    author: { displayName: string };
  };
  participants: Array<{
    userId: string;
    user: { id: string; displayName: string; handle: string; avatarUrl?: string };
  }>;
  unreadCount: number;
}

interface Message {
  id: string;
  content: string;
  authorId: string;
  createdAt: string;
  author: { id: string; displayName: string; handle: string; avatarUrl?: string };
  media?: Array<{ url: string; type: string }>;
}

export default function ChatPage() {
  const { chatId } = useParams();
  const [searchParams] = useSearchParams();
  const [chats, setChats] = useState<Chat[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user, token } = useAuthStore();
  const wsRef = useRef<WebSocket | null>(null);
  
  // Load chats
  useEffect(() => {
    const loadChats = async () => {
      try {
        let res = await api.chats.getAll();
        const dmUserId = searchParams.get('user');
        if (dmUserId) {
          const dm = await api.chats.createDM(dmUserId);
          if (dm?.chatId) {
            res = await api.chats.getAll();
            const fromDm = res.find((c: Chat) => c.id === dm.chatId);
            if (fromDm) {
              setSelectedChat(fromDm);
            }
          }
        }
        setChats(res);
        
        // Select chat from URL or first chat
        if (chatId) {
          const chat = res.find((c: Chat) => c.id === chatId);
          if (chat) {
            setSelectedChat(chat);
          } else if (res.length > 0) {
            setSelectedChat(res[0]);
          }
        } else if (!dmUserId && res.length > 0) {
          setSelectedChat(res[0]);
        }
      } catch (err) {
        console.error('Failed to load chats:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadChats();
  }, [chatId, searchParams]);
  
  // Load messages when chat selected
  useEffect(() => {
    if (!selectedChat) return;
    
    const loadMessages = async () => {
      try {
        const res = await api.chats.getMessages(selectedChat.id);
        setMessages(res);
        scrollToBottom();
      } catch (err) {
        console.error('Failed to load messages:', err);
      }
    };
    
    loadMessages();
  }, [selectedChat?.id]);
  
  // WebSocket for realtime messages
  useEffect(() => {
    if (!selectedChat || !token) return;
    
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      // Subscribe to chat
      ws.send(JSON.stringify({ type: 'subscribe_chat', chatId: selectedChat.id }));
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if ((data.type === 'message' || data.type === 'new_message') && data.chatId === selectedChat.id) {
          void api.chats.getMessages(selectedChat.id).then((items) => {
            setMessages(items);
            scrollToBottom();
          });
        }
      } catch (error) {
        console.error('Invalid websocket payload:', error);
      }
    };
    
    wsRef.current = ws;
    
    return () => {
      ws.close();
    };
  }, [selectedChat?.id, token]);
  
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  
  const handleSend = async () => {
    if (!newMessage.trim() || !selectedChat || sending) return;
    
    setSending(true);
    try {
      const message = await api.chats.sendMessage(selectedChat.id, { content: newMessage });
      setMessages((prev) => [...prev, message]);
      setNewMessage('');
      scrollToBottom();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };
  
  const getChatName = (chat: Chat) => {
    if (chat.name) return chat.name;
    if (chat.type === 'dm') {
      const other = chat.participants.find((p) => p.userId !== user?.id);
      return other?.user.displayName || 'Unknown';
    }
    return chat.participants.map((p) => p.user.displayName).join(', ');
  };
  
  const getChatAvatar = (chat: Chat) => {
    if (chat.type === 'dm') {
      const other = chat.participants.find((p) => p.userId !== user?.id);
      return other?.user;
    }
    return null;
  };
  
  return (
    <div className="h-screen flex">
      {/* Chat list */}
      <div className={`w-full lg:w-80 border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col ${selectedChat ? 'hidden lg:flex' : 'flex'}`}>
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">Chats</h1>
          <button
            onClick={() => setShowNewChat(true)}
            className="p-2 text-primary-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
            title="New chat"
          >
            <PlusIcon className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-12 h-12 rounded-full bg-slate-200 dark:bg-slate-700" />
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24 mb-2" />
                    <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-32" />
                  </div>
                </div>
              ))}
            </div>
          ) : chats.length === 0 ? (
            <div className="p-8 text-center">
              <ChatIcon className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400 mb-4">No chats yet</p>
              <button
                onClick={() => setShowNewChat(true)}
                className="btn btn-primary"
              >
                Start a chat
              </button>
            </div>
          ) : (
            chats.map((chat) => {
              const avatar = getChatAvatar(chat);
              return (
                <button
                  key={chat.id}
                  onClick={() => setSelectedChat(chat)}
                  className={`w-full p-4 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${
                    selectedChat?.id === chat.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  <div className="avatar avatar-lg flex-shrink-0">
                    {avatar?.avatarUrl ? (
                      <img src={avatar.avatarUrl} alt="" className="w-full h-full object-cover" />
                    ) : chat.type === 'group' ? (
                      <GroupIcon className="w-6 h-6" />
                    ) : (
                      getChatName(chat).charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-slate-900 dark:text-white truncate">
                        {getChatName(chat)}
                      </h3>
                      {chat.lastMessage && (
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                          {formatDistanceToNow(new Date(chat.lastMessage.createdAt))}
                        </span>
                      )}
                    </div>
                    {chat.lastMessage && (
                      <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                        {chat.lastMessage.author.displayName}: {chat.lastMessage.content}
                      </p>
                    )}
                  </div>
                  {chat.unreadCount > 0 && (
                    <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {chat.unreadCount}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
      
      {/* Chat view */}
      {selectedChat ? (
        <div className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-900">
          {/* Chat header */}
          <div className="p-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
            <button
              onClick={() => setSelectedChat(null)}
              className="lg:hidden p-2 -ml-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              <BackIcon className="w-5 h-5" />
            </button>
            <div className="avatar">
              {getChatAvatar(selectedChat)?.avatarUrl ? (
                <img src={getChatAvatar(selectedChat)!.avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : selectedChat.type === 'group' ? (
                <GroupIcon className="w-5 h-5" />
              ) : (
                getChatName(selectedChat).charAt(0).toUpperCase()
              )}
            </div>
            <div className="flex-1">
              <h2 className="font-medium text-slate-900 dark:text-white">
                {getChatName(selectedChat)}
              </h2>
              {selectedChat.type === 'group' && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {selectedChat.participants.length} members
                </p>
              )}
            </div>
            <button className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
              <MoreIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => {
              const isOwn = message.authorId === user?.id;
              const showAvatar = !isOwn && (index === 0 || messages[index - 1].authorId !== message.authorId);
              
              return (
                <div
                  key={message.id}
                  className={`flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}
                >
                  {!isOwn && (
                    <div className={`w-8 h-8 flex-shrink-0 ${showAvatar ? '' : 'invisible'}`}>
                      <div className="avatar avatar-sm">
                        {message.author.avatarUrl ? (
                          <img src={message.author.avatarUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          (message.author.displayName || 'U').charAt(0).toUpperCase()
                        )}
                      </div>
                    </div>
                  )}
                  <div
                    className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}
                  >
                    {showAvatar && !isOwn && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1 ml-1">
                        {message.author.displayName}
                      </p>
                    )}
                    <div
                      className={`px-4 py-2 rounded-2xl ${
                        isOwn
                          ? 'bg-primary-500 text-white rounded-br-md'
                          : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-bl-md'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>
                    <p className={`text-xs text-slate-400 mt-1 ${isOwn ? 'text-right mr-1' : 'ml-1'}`}>
                      {formatTime(new Date(message.createdAt))}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Message input */}
          <div className="p-4 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <button className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                <AttachIcon className="w-5 h-5" />
              </button>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder="Type a message..."
                className="input flex-1"
              />
              <button
                onClick={handleSend}
                disabled={!newMessage.trim() || sending}
                className="btn btn-primary p-2.5"
              >
                <SendIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="hidden lg:flex flex-1 items-center justify-center bg-slate-50 dark:bg-slate-900">
          <div className="text-center">
            <ChatIcon className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">Select a chat to start messaging</p>
          </div>
        </div>
      )}
      
      {/* New chat modal */}
      {showNewChat && (
        <NewChatModal
          onClose={() => setShowNewChat(false)}
          onChatCreated={(chat) => {
            setChats((prev) => [chat, ...prev]);
            setSelectedChat(chat);
            setShowNewChat(false);
          }}
        />
      )}
    </div>
  );
}

function NewChatModal({ onClose, onChatCreated }: { onClose: () => void; onChatCreated: (chat: Chat) => void }) {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    const searchUsers = async () => {
      if (!search.trim()) {
        setUsers([]);
        return;
      }
      
      try {
        const res = await api.users.search({ query: search });
        setUsers(res);
      } catch (err) {
        console.error('Failed to search users:', err);
      }
    };
    
    const timer = setTimeout(searchUsers, 300);
    return () => clearTimeout(timer);
  }, [search]);
  
  const handleCreate = async () => {
    if (selectedUsers.length === 0) return;
    
    setLoading(true);
    try {
      const chat = await api.chats.create({
        type: selectedUsers.length === 1 ? 'dm' : 'group',
        name: selectedUsers.length > 1 ? groupName || undefined : undefined,
        participantIds: selectedUsers.map((u) => u.id),
      });
      onChatCreated(chat);
    } catch (err) {
      console.error('Failed to create chat:', err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md shadow-xl">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">New Chat</h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {selectedUsers.length > 1 && (
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Group name (optional)"
              className="input"
            />
          )}
          
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map((u) => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full text-sm"
                >
                  {u.displayName}
                  <button
                    onClick={() => setSelectedUsers((prev) => prev.filter((p) => p.id !== u.id))}
                    className="hover:text-primary-800 dark:hover:text-primary-200"
                  >
                    <CloseIcon className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          )}
          
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="input"
            autoFocus
          />
          
          <div className="max-h-60 overflow-y-auto">
            {users.map((u) => (
              <button
                key={u.id}
                onClick={() => {
                  if (!selectedUsers.find((s) => s.id === u.id)) {
                    setSelectedUsers((prev) => [...prev, u]);
                  }
                  setSearch('');
                }}
                disabled={selectedUsers.find((s) => s.id === u.id)}
                className="w-full p-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg disabled:opacity-50"
              >
                <div className="avatar">
                  {u.avatarUrl ? (
                    <img src={u.avatarUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    (u.displayName || 'U').charAt(0).toUpperCase()
                  )}
                </div>
                <div className="text-left">
                  <p className="font-medium text-slate-900 dark:text-white">{u.displayName}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">@{u.handle}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
        
        <div className="p-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={handleCreate}
            disabled={selectedUsers.length === 0 || loading}
            className="btn btn-primary w-full"
          >
            {loading ? 'Creating...' : selectedUsers.length > 1 ? 'Create Group' : 'Start Chat'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Icons
function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function GroupIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
    </svg>
  );
}

function AttachIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
    </svg>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
